// dxfExport.ts — R2000 DXF writer for panel and site plan export
// Produces valid AutoCAD R2000 DXF without external libraries.
// All values in inches to match v11 coordinate system.

import type { PanelData, CVProject } from '../types/panelvar';
import { outlineFromMp, makeDXFPolyline } from './panelGeometry';

// ── Layer definitions (mirror v11 drawpan.lsp layer names) ───────────────────
const LAYERS = [
  { name: 'PANEL',       color: 7,  ltype: 'CONTINUOUS' },   // white — panel outline
  { name: 'OPENINGS',    color: 4,  ltype: 'CONTINUOUS' },   // cyan  — windows/doors
  { name: 'BLOCKOUTS',   color: 3,  ltype: 'CONTINUOUS' },   // green — blockouts
  { name: 'FEATURES',    color: 5,  ltype: 'CONTINUOUS' },   // blue  — reveals/chamfers
  { name: 'STRUCTURAL',  color: 1,  ltype: 'CONTINUOUS' },   // red   — brace/pick/weld
  { name: 'DIMENSIONS',  color: 2,  ltype: 'CONTINUOUS' },   // yellow
  { name: 'NOTES',       color: 7,  ltype: 'CONTINUOUS' },
  { name: 'REVISIONS',   color: 6,  ltype: 'CONTINUOUS' },   // magenta
  { name: 'SITE',        color: 7,  ltype: 'CONTINUOUS' },
  { name: 'GRID',        color: 8,  ltype: 'DASHED' },
];

let _handle = 0x100;
function nextHandle(): string { return (_handle++).toString(16).toUpperCase(); }

function section(name: string, content: string): string {
  return `  0\nSECTION\n  2\n${name}\n${content}  0\nENDSEC\n`;
}

function layerDefs(): string {
  return LAYERS.map(l =>
    `  0\nLAYER\n  5\n${nextHandle()}\n100\nAcDbSymbolTableRecord\n100\nAcDbLayerTableRecord\n  2\n${l.name}\n 70\n     0\n 62\n     ${l.color}\n  6\n${l.ltype}\n`
  ).join('');
}

function line(x1: number, y1: number, x2: number, y2: number, layer: string): string {
  return `  0\nLINE\n  5\n${nextHandle()}\n  8\n${layer}\n 10\n${x1.toFixed(4)}\n 20\n${y1.toFixed(4)}\n 30\n0.0\n 11\n${x2.toFixed(4)}\n 21\n${y2.toFixed(4)}\n 31\n0.0\n`;
}

function rect(x: number, y: number, w: number, h: number, layer: string): string {
  return line(x, y, x + w, y, layer)
       + line(x + w, y, x + w, y + h, layer)
       + line(x + w, y + h, x, y + h, layer)
       + line(x, y + h, x, y, layer);
}

function circle(cx: number, cy: number, r: number, layer: string): string {
  return `  0\nCIRCLE\n  5\n${nextHandle()}\n  8\n${layer}\n 10\n${cx.toFixed(4)}\n 20\n${cy.toFixed(4)}\n 30\n0.0\n 40\n${r.toFixed(4)}\n`;
}

function text(x: number, y: number, height: number, value: string, layer: string, angle = 0): string {
  return `  0\nTEXT\n  5\n${nextHandle()}\n  8\n${layer}\n 10\n${x.toFixed(4)}\n 20\n${y.toFixed(4)}\n 30\n0.0\n 40\n${height.toFixed(4)}\n  1\n${value}\n 50\n${angle.toFixed(1)}\n 72\n     0\n`;
}

function dimLinear(x1: number, y1: number, x2: number, y2: number, dimY: number, layer: string): string {
  const mx = (x1 + x2) / 2;
  return `  0\nDIMENSION\n  5\n${nextHandle()}\n  8\n${layer}\n 70\n     32\n 10\n${mx.toFixed(4)}\n 20\n${dimY.toFixed(4)}\n 30\n0.0\n 13\n${x1.toFixed(4)}\n 23\n${y1.toFixed(4)}\n 33\n0.0\n 14\n${x2.toFixed(4)}\n 24\n${y2.toFixed(4)}\n 34\n0.0\n`;
}

function titleBlock(panel: PanelData): string {
  const mp = panel.mpvar;
  const tbX = 0, tbY = -48;  // Title block below panel
  let s = rect(tbX, tbY, 120, 48, 'NOTES');  // outer border
  // Dividers
  s += line(tbX, tbY + 24, tbX + 120, tbY + 24, 'NOTES');
  s += line(tbX + 60, tbY, tbX + 60, tbY + 24, 'NOTES');
  // Labels
  s += text(tbX + 2, tbY + 38, 3, 'PROJECT: ' + mp.projectName, 'NOTES');
  s += text(tbX + 2, tbY + 32, 3, 'DRAWING: ' + mp.drawingNumber, 'NOTES');
  s += text(tbX + 62, tbY + 38, 3, 'MARK: ' + mp.panelMark, 'NOTES');
  s += text(tbX + 62, tbY + 32, 3, 'DATE: ' + mp.date, 'NOTES');
  s += text(tbX + 2, tbY + 18, 3, 'DWN: ' + mp.drawBy, 'NOTES');
  s += text(tbX + 62, tbY + 18, 3, 'CHK: ' + mp.checkBy, 'NOTES');
  s += text(tbX + 2, tbY + 10, 3, 'REV: ' + mp.revision, 'NOTES');
  s += text(tbX + 62, tbY + 10, 3, 'THKNS: ' + mp.thickness + '"', 'NOTES');
  return s;
}

/** Build a panel DXF document as a string. Throws if dimensions are invalid. */
export function buildPanelDXFString(panel: PanelData): string {
  _handle = 0x100;
  const mp  = panel.mpvar;
  const w   = mp.width;
  const h   = mp.height;

  if (!w || !h || w <= 0 || h <= 0) {
    throw new Error(`Cannot export DXF: panel "${mp.panelMark || 'unnamed'}" has invalid dimensions (${w}" × ${h}").`);
  }

  let entities = '';

  // ── Panel outline (actual top shape polygon) ───────────────────────────────
  const outline = outlineFromMp(mp);
  entities += makeDXFPolyline(outline, 'PANEL', nextHandle);

  // ── Width + height dimensions ──────────────────────────────────────────────
  entities += dimLinear(0, 0, w, 0, -12, 'DIMENSIONS');
  entities += dimLinear(w, 0, w, h, w + 12, 'DIMENSIONS');

  // ── Panel mark label ────────────────────────────────────────────────────────
  entities += text(w / 2 - 6, h / 2, 6, mp.panelMark, 'NOTES');

  // ── Rough openings ─────────────────────────────────────────────────────────
  panel.rovar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    entities += rect(d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'OPENINGS');
    entities += text(d.x, d.y, 3, d.mark || 'RO', 'NOTES');
  });

  // ── Standard openings (windows) ───────────────────────────────────────────
  panel.wdvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    entities += rect(d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'OPENINGS');
    entities += text(d.x, d.y + d.height / 2 + 3, 2.5, d.mark || 'W', 'NOTES');
  });

  // ── Man doors ─────────────────────────────────────────────────────────────
  panel.drvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    entities += rect(d.x - d.width / 2, d.y, d.width, d.height, 'OPENINGS');
    entities += text(d.x, d.height / 2, 2.5, d.mark || 'D', 'NOTES');
  });

  // ── Dock levelers ─────────────────────────────────────────────────────────
  panel.dlvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    entities += rect(d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'BLOCKOUTS');
    entities += text(d.x, d.y, 2.5, d.mark || 'DL', 'NOTES');
  });

  // ── Rectangular blockouts ─────────────────────────────────────────────────
  panel.sbvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    entities += rect(d.x - d.width / 2, d.y - d.height / 2, d.width, d.height, 'BLOCKOUTS');
    entities += text(d.x, d.y, 2.5, d.mark || 'SB', 'NOTES');
  });

  // ── Round blockouts ───────────────────────────────────────────────────────
  panel.rbvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    entities += circle(d.x, d.y, d.diameter / 2, 'BLOCKOUTS');
    entities += text(d.x, d.y, 2.5, d.mark || 'RB', 'NOTES');
  });

  // ── Horizontal feature strips ──────────────────────────────────────────────
  panel.fhvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    const xS = d.xEnd > 0 ? d.xStart : 0;
    const xE = d.xEnd > 0 ? d.xEnd   : w;
    entities += rect(xS, d.y, xE - xS, d.height, 'FEATURES');
  });

  // ── Vertical feature strips ────────────────────────────────────────────────
  panel.fvvar.forEach((slot) => {
    if (!slot.enabled) return;
    const d = slot.data;
    const yS = d.yEnd > 0 ? d.yStart : 0;
    const yE = d.yEnd > 0 ? d.yEnd   : h;
    entities += rect(d.x - d.width / 2, yS, d.width, yE - yS, 'FEATURES');
  });

  // ── Brace points ──────────────────────────────────────────────────────────
  panel.bpvar.forEach((slot) => {
    if (!slot.enabled) return;
    entities += circle(slot.data.x, slot.data.y, 3, 'STRUCTURAL');
    entities += text(slot.data.x + 4, slot.data.y, 2.5, slot.data.mark || 'BP', 'NOTES');
  });

  // ── Pick points ───────────────────────────────────────────────────────────
  panel.ppvar.forEach((slot) => {
    if (!slot.enabled) return;
    entities += circle(slot.data.x, slot.data.y, 4, 'STRUCTURAL');
    entities += text(slot.data.x + 5, slot.data.y, 2.5, slot.data.mark || 'PP', 'NOTES');
  });

  // ── Title block ───────────────────────────────────────────────────────────
  entities += titleBlock(panel);

  return buildDXF(entities);
}

export function panelDXFFilename(panel: PanelData): string {
  return `${panel.mpvar.panelMark || 'panel'}.dxf`;
}

/** Build a site DXF document as a string. Throws if site is not configured. */
export function buildSiteDXFString(project: CVProject): string {
  if (!project.site) {
    throw new Error('No site plan configured.');
  }
  _handle = 0x100;
  const site = project.site;
  const scale = 12; // 1ft = 12 DXF units (inches scale)
  let entities = '';

  site.panels.forEach((placement) => {
    const panel = project.panels.find(p => p.id === placement.panelId);
    if (!panel) return;
    const mp = panel.mpvar;
    const cx = placement.x * scale;
    const cy = placement.y * scale;
    const pw = mp.width / 2;
    const pt = mp.thickness / 2;
    entities += rect(cx - pw, cy - pt, mp.width, mp.thickness, 'SITE');
    entities += text(cx - 6, cy - 1.5, 3, mp.panelMark, 'NOTES');
  });

  // Grid lines
  const size = 600;
  for (let i = 0; i <= size; i += site.gridSpacing * scale) {
    entities += line(i, 0, i, size, 'GRID');
    entities += line(0, i, size, i, 'GRID');
  }

  return buildDXF(entities);
}

export function siteDXFFilename(project: CVProject): string {
  const site = project.site;
  const base = (site?.projectName || project.name || 'site').replace(/\s+/g, '_');
  return `${base}_site.dxf`;
}

function buildDXF(entities: string): string {
  return `  0\nSECTION\n  2\nHEADER\n  9\n$ACADVER\n  1\nAC1015\n  9\n$INSUNITS\n 70\n     1\n  0\nENDSEC\n`
       + section('TABLES',
           `  0\nTABLE\n  2\nLAYER\n  5\n${nextHandle()}\n 70\n${LAYERS.length}\n${layerDefs()}  0\nENDTAB\n`)
       + section('ENTITIES', entities)
       + `  0\nEOF\n`;
}

